import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg, fmtL } from "@/lib/api";
import { useClubs } from "@/pages/LiveDisplay";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Pick = ({ label, value, onChange, items, testid, placeholder }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">{items.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export function ManualSaleDialog({ open, onOpenChange, onDone }) {
  const clubs = useClubs() || [];
  const [v, setV] = useState({ club: "", player: "", team: "", amount: "", reason: "" });
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (x) => setV((s) => ({ ...s, [k]: x }));
  useEffect(() => { if (open) setV({ club: "", player: "", team: "", amount: "", reason: "" }); }, [open]);
  useEffect(() => {
    if (!v.club) return;
    api.get("/players").then((r) => setPlayers(r.data.filter((p) => p.club_id === v.club && !p.archived && p.auction_status !== "sold")));
    api.get(`/auction/${v.club}/state`).then((r) => setTeams(r.data.teams));
  }, [v.club]);
  const team = teams.find((t) => t.id === v.team);
  const submit = async () => {
    if (!v.club || !v.player || !v.team || !(Number(v.amount) > 0) || v.reason.trim().length < 3) return toast.error("Fill club, player, team, amount and a reason");
    setBusy(true);
    try {
      await api.post(`/auction/${v.club}/manual-sale`, { player_id: v.player, team_id: v.team, amount: Number(v.amount), reason: v.reason });
      toast.success(`Player assigned to ${team?.name} for ${fmtL(Number(v.amount))}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1323] border-[#E5B842]/20 max-w-xl" data-testid="manual-sale-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Manual sale / adjustment</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Pick label="Auction" value={v.club} onChange={(c) => setV((s) => ({ ...s, club: c, player: "", team: "" }))} testid="manual-club-select" placeholder="Choose club"
            items={clubs.map((c) => ({ value: c.id, label: c.name }))} />
          <Pick label="Player" value={v.player} onChange={set("player")} testid="manual-player-select" placeholder={v.club ? "Choose player" : "Choose club first"}
            items={players.map((p) => ({ value: p.id, label: `${p.lot_no ? `#${p.lot_no} ` : ""}${p.name} · ${p.auction_status}` }))} />
          <Pick label="Winning team" value={v.team} onChange={set("team")} testid="manual-team-select" placeholder="Choose team"
            items={teams.map((t) => ({ value: t.id, label: `${t.name} · ${fmtL(t.remaining_purse)} left · ${t.players_count} players` }))} />
          <div className="space-y-1.5">
            <Label>Purchase amount (pts)</Label>
            <Input type="number" min="1" value={v.amount} onChange={(e) => set("amount")(e.target.value)} data-testid="manual-amount-input" placeholder="e.g. 450000" />
            {Number(v.amount) > 0 && <p className="text-[11px] text-slate-400">{fmtL(Number(v.amount))}{team && ` · purse after: ${fmtL(team.remaining_purse - Number(v.amount))}`}</p>}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Reason (saved in audit log)</Label>
            <Input value={v.reason} onChange={(e) => set("reason")(e.target.value)} data-testid="manual-reason-input" placeholder="e.g. Bid recorded offline during network issue" />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full border border-white/10 text-sm" data-testid="manual-cancel-btn">Cancel</button>
          <button disabled={busy} onClick={submit} className="px-5 py-2 rounded-full gold-bg font-bold text-sm flex items-center gap-2" data-testid="manual-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Assign & debit purse</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
